<template>
  <div>
    <!--
      Full-screen barrier: generic loading OR Flutter-parity stock download.
      Stock path stays up through Phase 2 even after getAllStock page-0 unlock.
    -->
    <loading
      :active="overlayActive"
      :can-cancel="false"
      :is-full-page="true"
    >
      <div class="loader-body d-flex flex-column justify-content-center align-items-center">
        <img
          style="width: 150px; height: 150px"
          :src="loaderImage"
          class="rotate"
          alt=""
        />
        <br>
        <span class="fw-bolder text-center text-dark-emphasis text-uppercase loader-title">
          {{ titleText }}
        </span>
        <span
          v-if="showStockPhase"
          class="loader-phase text-center"
        >
          {{ stockProgress.message }}
        </span>

        <!-- Flutter StoreSyncLoadingView: determinate bar when total known -->
        <div
          v-if="showStockProgress"
          class="stock-progress"
          role="progressbar"
          :aria-valuenow="percentValue"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="stockProgress.message || 'Downloading stock'"
        >
          <div
            class="stock-progress-track"
            :class="{ indeterminate: !hasStockTotal }"
          >
            <div
              class="stock-progress-bar"
              :style="barStyle"
            />
          </div>
          <template v-if="hasStockTotal">
            <div class="stock-progress-pct">
              {{ percentLabel }} of {{ formatCount(stockProgress.total) }}
            </div>
            <div class="stock-progress-counts">
              {{ formatCount(stockProgress.loaded) }} of {{ formatCount(stockProgress.total) }} items
            </div>
          </template>
        </div>

        <div
          v-else
          id="wave"
        >
          <span class="dot" />
          <span class="dot" />
          <span class="dot" />
        </div>
      </div>
    </loading>
  </div>
</template>
<script>
import loaderImage from "@/assets/favicon.ico";

/**
 * Global full-page loader.
 * When stock full-sync is active (Vuex getStockSyncProgress), chrome matches
 * Flutter StoreSyncLoadingView: phase label + linear bar + loaded/total.
 */
export default {
  name: 'Loader',
  data() {
    return {
      loaderImage
    }
  },
  computed: {
    genericLoading() {
      return !!this.$store.getters.getLoadingState;
    },
    stockProgress() {
      return this.$store.getters.getStockSyncProgress || {
        active: false,
        message: '',
        loaded: 0,
        total: 0,
        fraction: null,
      };
    },
    stockActive() {
      return !!this.stockProgress.active;
    },
    overlayActive() {
      return this.genericLoading || this.stockActive;
    },
    showStockProgress() {
      return this.stockActive;
    },
    hasStockTotal() {
      return Number(this.stockProgress.total) > 0;
    },
    showStockPhase() {
      // Hide duplicate line when phase equals the primary title.
      if (!this.stockActive) return false;
      const msg = String(this.stockProgress.message || '').trim();
      if (!msg) return false;
      return msg.toLowerCase() !== String(this.titleText || '').toLowerCase();
    },
    titleText() {
      if (this.stockActive) {
        return 'Preparing store...';
      }
      return 'Please Wait...';
    },
    /**
     * 0–100 for aria + percent label; null when indeterminate.
     * While stock overlay is active and not finished, never show 100%
     * (download/save still running — avoids "100% of 10,000" while working).
     */
    percentValue() {
      if (!this.hasStockTotal) return null;
      const frac = this.stockProgress.fraction != null
        ? Number(this.stockProgress.fraction)
        : (Number(this.stockProgress.loaded) / Number(this.stockProgress.total));
      if (!Number.isFinite(frac)) return 0;
      let pct = Math.min(100, Math.max(0, Math.round(frac * 100)));
      if (this.stockActive && !this.stockProgress.finished && pct >= 100) {
        pct = 99;
      }
      return pct;
    },
    percentLabel() {
      const p = this.percentValue;
      return p == null ? '' : `${p}%`;
    },
    barStyle() {
      if (!this.hasStockTotal) {
        return {};
      }
      const w = this.percentValue == null ? 0 : this.percentValue;
      return { width: `${w}%` };
    },
  },
  methods: {
    /**
     * Comma-group integers like Flutter StoreSyncLoadingView.formatCount.
     * @param {number|string} n
     * @returns {string}
     */
    formatCount(n) {
      const s = String(Math.max(0, Math.floor(Number(n) || 0)));
      let out = '';
      for (let i = 0; i < s.length; i += 1) {
        if (i > 0 && (s.length - i) % 3 === 0) out += ',';
        out += s[i];
      }
      return out;
    },
  },
}
</script>
<style lang="scss" scoped>
.loader-body {
  max-width: 360px;
  margin: 0 auto;
  padding: 0 24px;
}

.loader-title {
  letter-spacing: 0.02em;
}

.loader-phase {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #5c5c5c;
}

.stock-progress {
  margin-top: 20px;
  width: 280px;
  max-width: 100%;
}

.stock-progress-track {
  height: 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.12);
  overflow: hidden;

  &.indeterminate .stock-progress-bar {
    width: 40%;
    animation: stock-indeterminate 1.2s ease-in-out infinite;
  }
}

.stock-progress-bar {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(to right, #339CFF, #A529FF);
  transition: width 0.25s ease-out;
}

.stock-progress-pct {
  margin-top: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
  color: #303131;
}

.stock-progress-counts {
  margin-top: 4px;
  text-align: center;
  font-size: 0.85rem;
  color: #5c5c5c;
}

@keyframes stock-indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

.rotate {
  border-radius: 50%;

  box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
  transform: scale(1);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

div#wave {
  position: relative;
  margin-top: 5px;
  text-align: center;
  width: 100px;
  height: 100px;
  margin-left: auto;
  margin-right: auto;

  .dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 3px;
    background: #303131;
    animation: wave 1.3s linear infinite;

    &:nth-child(2) {
      animation-delay: -1.1s;
    }

    &:nth-child(3) {
      animation-delay: -0.9s;
    }
  }
}

@keyframes wave {
  0%, 60%, 100% {
    transform: initial;
  }

  30% {
    transform: translateY(-15px);
  }
}
</style>
